import React from "react";
import { motion } from "framer-motion";
import { Sprout, Utensils, GraduationCap, TreeDeciduous, Sparkles } from "lucide-react";
import { PublicLayout } from "../components/Layout";

const CHAPTERS = [
  {
    id: "growing-together", tag: "01", title: "Growing Together",
    icon: <Sparkles size={22} />, color: "#12355B",
    body: "We believe industries should return more to society than they take. From the villages that surround our operations to the ports that carry our shipments, R K STEEL invests in the communities that made our seven-decade journey possible.",
  },
  {
    id: "feeding-communities", tag: "02", title: "Feeding Communities",
    icon: <Utensils size={22} />, color: "#2F9E44",
    body: "Community food distribution every six months. Warm meals delivered with dignity to families in need across the regions we operate in — a simple, consistent act repeated with care.",
  },
  {
    id: "educating-futures", tag: "03", title: "Educating Futures",
    icon: <GraduationCap size={22} />, color: "#B8823E",
    body: "Educational support for the children of our employees — because the families that build R K STEEL deserve to see their next generation flourish. Scholarships continue year after year, quietly changing lives.",
  },
  {
    id: "greener-tomorrow", tag: "04", title: "Greener Tomorrow",
    icon: <TreeDeciduous size={22} />, color: "#2F9E44",
    body: "More than 1,000 forest trees planted across the Western Ghats, with new plantations added every year to strengthen biodiversity and restore ecological balance — because a recycling company owes it to the planet.",
    highlight: "1,000+ trees planted across the Western Ghats",
  },
];

const Philanthropy = () => (
  <PublicLayout>
    <section className="pt-28 md:pt-32 pb-16 md:pb-20 bg-[#F7F8F5] relative">
      <div className="grid-texture absolute inset-0 opacity-60" />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 relative">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#2F9E44] mb-4">Philanthropy</div>
        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-[#222629] max-w-3xl leading-[1.05]">
          Steel builds cities. <br /> <span className="text-[#12355B]">Values build legacies.</span>
        </h1>
        <p className="mt-5 md:mt-6 text-base md:text-lg text-[#5B6675] max-w-2xl leading-relaxed">
          R K STEEL believes that true success is measured not only by what we build, but by what we give back. From the villages that surround our operations to the ports that connect us to the world, we remain committed to investing in the communities that have been part of our journey for more than seven decades.
        </p>
      </div>
    </section>

    {/* Chapters */}
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-5 sm:px-6 md:px-10 space-y-16 md:space-y-24">
        {CHAPTERS.map((c, i) => {
          const flip = i % 2 === 1;
          return (
            <motion.div
              key={c.id}
              data-testid={`philanthropy-chapter-${c.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className={`grid md:grid-cols-12 gap-8 md:gap-14 items-center ${flip ? "md:[direction:rtl]" : ""}`}
            >
              <div className={`md:col-span-5 md:[direction:ltr] flex ${flip ? "md:justify-end" : ""}`}>
                <div className="glass-card rounded-3xl p-8 md:p-10 max-w-md relative overflow-hidden">
                  <div className="grid-texture absolute inset-0 opacity-40" />
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center text-white" style={{ background: c.color }}>
                      {c.icon}
                    </div>
                    <div className="mt-6 font-display text-6xl font-light text-[#12355B]/15 tracking-tighter leading-none">{c.tag}</div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-7 md:[direction:ltr]">
                <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: c.color }}>Chapter {c.tag}</div>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#222629] mt-2 tracking-tight leading-[1.1]">
                  {c.title}
                </h2>
                <p className="mt-5 text-base md:text-lg text-[#5B6675] leading-relaxed max-w-xl">{c.body}</p>
                {c.highlight && (
                  <div className="mt-6 inline-flex items-center gap-3 rounded-full pl-3 pr-5 py-2 bg-[#2F9E44]/10 border border-[#2F9E44]/30">
                    <TreeDeciduous size={16} className="text-[#2F9E44]" />
                    <span className="text-sm font-heading font-semibold text-[#2F9E44]">{c.highlight}</span>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>

    {/* Philosophy */}
    <section data-testid="philosophy-section" className="py-20 md:py-28 bg-[#222629] text-white relative overflow-hidden">
      <div className="grid-texture-dark absolute inset-0" />
      <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-10 text-center relative">
        <div className="flex justify-center gap-3 mb-8 text-[#B8823E]">
          <Sprout size={22} /><Utensils size={22} /><GraduationCap size={22} /><TreeDeciduous size={22} />
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#B8823E] mb-6">Our Philosophy</div>
        <div className="font-heading text-2xl md:text-3xl lg:text-4xl font-medium leading-[1.35] tracking-tight space-y-3">
          <p>Every tonne recycled saves resources.</p>
          <p>Every tree planted restores balance.</p>
          <p>Every child educated builds tomorrow.</p>
          <p className="text-[#B8823E]">Every act of responsibility shapes the future.</p>
        </div>
        <p className="mt-10 text-xs md:text-sm text-[#B8823E] tracking-[0.24em] uppercase">— R K STEEL, since 1955</p>
      </div>
    </section>
  </PublicLayout>
);

export default Philanthropy;
